import { trackFailedToStartChunkRecording } from '@/lib/analytics/scribe/general';
import { sessionStartRecording } from '@/lib/analytics/scribe/session';
import { useQueryClient } from '@tanstack/react-query';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  activeRecordingTransportAtom,
  CHRONICLE_ORIGIN,
  chronicleDeviceStateAtom,
  chronicleRecordingOriginAtom,
  chronicleSessionIdAtom,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import { chronicleSupportsChunkLiveAtom } from '@/atoms/chronicle/chunk-live';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { useIsAppsUseTranscriptionServiceV2DesktopEnabled } from '@/features/flags';
import { useIsAppsRustAuth0TokenRefreshDesktopEnabled } from '@/features/flags/rust-auth0-token-refresh-desktop';
import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { useCurrentInputLanguages } from '@/hooks/useCurrentInputLanguages';
import { setToken } from '@/lib/authActions';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  finishRecordingAttempt,
  setRecordingAttemptTransport,
  type RecordingAttemptTracking,
  type RecordingAttemptTrackingKey,
} from '@/lib/telemetry/recordingStartTracking';
import { type Dispatch } from '@/store';
import { releaseIdleStreamForCapture } from '@/tauri/audio-recorder/idle-stream-handover';
import { desktopAuthBridge } from '@/tauri/auth0';
import { chronicleSessionBridge } from '@/tauri/chronicle/session';
import {
  resolveEffectiveTranscriptionService,
  TRANSCRIPTION_V2_FALLBACK_TAG,
} from '@/tauri/transcription/effective-service';
import { isTauri } from '@/tauri/utils';
import { type CreateLiveTranscriptionResponse } from '@/types';
import {
  trackHardwareRecordingStart,
  trackHardwareRecordingStartFailed,
} from '@/utils/analytics/track-hardware-events';
import {
  mapRouteToPipeline,
  trackRemotePipelineSelected,
  trackRemoteRecordingStartBlocked,
} from '@/utils/analytics/track-remote-device-events';
import { trackSessionDetailResumeStarted } from '@/utils/analytics/track-session-resume-events';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { recordingIdRequestConfig } from '@/utils/http/recordingIdRequest';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { getScribeBaseUrlFromV2, scribeHttpV2 } from '@/utils/scribeHTTP';
import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { getActiveTrackings } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type SessionResponse } from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';
import { defineMessages } from 'react-intl';

// Total cap on the v1 recording id request, covering every retry attempt.
const RECORDING_ID_REQUEST_DEADLINE_MS = 60 * 1000;

/**
 * The v1 request that mints a recording id, capped as a whole: each attempt has
 * its own timeout, and a stalled start would otherwise hold `isStarting` through
 * every retry.
 */
const requestRecordingId = async (sessionId: string) => {
  const deadline = new AbortController();
  const deadlineTimer = setTimeout(
    () => deadline.abort(),
    RECORDING_ID_REQUEST_DEADLINE_MS
  );
  try {
    return await scribeHttpV2.post<CreateLiveTranscriptionResponse>(
      `/sessions/${sessionId}/restful-segment-transcription`,
      undefined,
      recordingIdRequestConfig(sessionId, { signal: deadline.signal })
    );
  } catch (error) {
    // Axios words an abort as just "canceled", which reads like a user abort in analytics.
    if (deadline.signal.aborted) throw new Error('Timed out generating recording id');
    throw error;
  } finally {
    clearTimeout(deadlineTimer);
  }
};

const startRecordingErrorMessages = defineMessages({
  contactIt: {
    defaultMessage: 'Please try again later or contact your IT team.',
    id: 'aWxOAKCArE',
  },
});

/**
 * Closes only the click-site entry the caller handed over. The resume re-arm
 * owns its RESUME entry and device starts open none, so both pass no key.
 */
const closeOwnedStartTracking = (
  tracking: RecordingAttemptTracking | undefined,
  error?: unknown
): void => {
  if (!tracking) return;
  if (error === undefined) finishRecordingAttempt(tracking);
  else finishRecordingAttempt(tracking, { outcome: 'failed', error });
};

/**
 * APP-3779 (davejcampbell review): Rust `register_v2` (`session/upload_client.rs`)
 * is synchronous inside `chronicle_session_start` and does not auto-retry on
 * 401. Chunk uploads have their own pause/resume token-refresh dance, but
 * session-start doesn't — a stale token at this exact moment becomes a
 * hard "session failed to start" toast.
 *
 * This helper force-refreshes the Tauri auth0 session and writes the new
 * token back into Redux. Used as the **recovery** path: called on
 * `EKS_REGISTER_UNAUTHORIZED` to drive a single retry of the IPC. (The former
 * pre-emptive call before `chronicle_session_start` was removed in APP-4062 —
 * it put a forced auth0 round-trip on the device-start critical path; a stale
 * token at session start is now handled reactively by that same retry.)
 *
 * Returns the fresh token or `null` if refresh failed (caller should fall
 * back to whatever's currently in Redux + surface a normal start failure).
 */
async function forceRefreshDesktopAuthToken(): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const response = await desktopAuthBridge.getAccessToken({ forceRefresh: true });
    const freshToken = response.accessToken;
    if (!freshToken) {
      logger.warn({
        tag: 'chronicle_force_refresh_no_token',
        event_type: 'CHRONICLE_FORCE_REFRESH_NO_TOKEN',
        details: { reason: 'auth0 plugin returned no access_token' },
      });
      return null;
    }
    setToken(freshToken);
    return freshToken;
  } catch (err) {
    logger.warn({
      tag: 'chronicle_force_refresh_failed',
      event_type: 'CHRONICLE_FORCE_REFRESH_FAILED',
      details: { error: err instanceof Error ? err.message : String(err) },
    });
    return null;
  }
}

/**
 * What a start attempt actually did. This resolves on paths that started
 * NOTHING, so a caller that reads "resolved" as "started" reports a success
 * that never happened — see the resume re-arm in useChronicleResumeRecording.
 * A genuine failure still throws.
 */
export type ChronicleStartOutcome =
  | 'started'
  | 'already_starting'
  | 'already_active'
  | 'no_session';

/**
 * Core hook for starting a Chronicle recording session.
 *
 * Borrows session creation + recording ID generation logic from
 * `useMutateStartTauriRecording`, adapted for the Chronicle flow:
 *
 * 1. Generate recording ID via backend API
 * 2. Get auth token + base URL for Rust upload pipeline
 * 3. Stop desktop idle stream (release system mic, free resources)
 * 4. Start session in Rust (`chronicle_session_start`) — this single call
 *    configures the upload pipeline AND starts recording on the Chronicle
 *    device (idempotent for device-initiated starts where the device is
 *    already recording)
 * 5. Update Jotai atoms (sessionId, origin)
 * 6. Update session recording status in query cache
 * 7. Start timer — offset by existing session duration plus elapsed since
 *    Chronicle `startedAt` (device-initiated)
 *
 * Key differences from the desktop flow (`useMutateStartTauriRecording`):
 * - No mic/screen-capture permission checks (Chronicle handles its own audio)
 * - No input/output device validation (Chronicle IS the device)
 * - No FE chunk upload interval (upload pipeline runs in Rust via src-tauri)
 * - No `tauriRecordModel.setChunks` (Rust tracks chunks internally)
 * - No direct device commands — Rust session layer handles device start/stop
 *
 * Called from both:
 * - Desktop UI click (via `useRecording` → origin: 'desktop_app')
 * - Device-initiated start (via `useChronicleSessionControl` → origin: 'remote_device')
 */
export function useChronicleStartRecording() {
  const { t } = useTranslations();
  const sessionId = useCurrentSessionId();
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const setChronicleSessionId = useSetAtom(chronicleSessionIdAtom);
  const setRecordingOrigin = useSetAtom(chronicleRecordingOriginAtom);
  const setActiveRecordingTransport = useSetAtom(activeRecordingTransportAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const [isStarting, setIsStarting] = useState(false);
  // APP-3779: lock the v2 boolean for this hook invocation. The useCallback
  // closure below captures the resolved value; flag flips after callback
  // creation don't affect an in-flight session because the Rust online
  // session also locks the choice (see `OnlineSession::start_session`).
  const useTranscriptionServiceV2 = useIsAppsUseTranscriptionServiceV2DesktopEnabled();
  // Lock Rust Auth0 force-refresh for this session (Rust locks it too).
  const rustAuth0TokenRefresh = useIsAppsRustAuth0TokenRefreshDesktopEnabled();
  const languages = useCurrentInputLanguages();

  const startChronicleRecording = useCallback(
    async (args: {
      origin: ChronicleRecordingOrigin;
      targetSessionId?: string;
      /** ISO-8601 timestamp from Chronicle device for timer sync (device-initiated) */
      startedAt?: string;
      /**
       * HAR-1626: elapsed seconds already on the clock, passed by a pause→resume
       * re-arm. A remote-first recording never gets a Rust online session
       * (`auto_session.rs` skips pre-existing ones), so resume lands here — and
       * its backend `duration` stays 0 until bulk sync, which would restart the
       * timer from zero. Used as a floor, never an override.
       */
      resumeFromSeconds?: number;
      /** The click-site tracking entry this call reports; omit when the caller owns none. */
      tracking?: RecordingAttemptTracking;
      /** @deprecated Pass `tracking`; retained for direct callers during migration. */
      trackingKey?: RecordingAttemptTrackingKey;
    }): Promise<ChronicleStartOutcome> => {
      // Snapshot key-only callers synchronously so later native callbacks can
      // never close a retry that replaced this entry.
      args.tracking ??= args.trackingKey
        ? {
            trackingKey: args.trackingKey,
            trackingInstanceId:
              getActiveTrackings().get(args.trackingKey)?.instanceId ?? null,
          }
        : undefined;
      setRecordingAttemptTransport(args.tracking, 'remote_device');
      const effectiveSessionId = args.targetSessionId ?? sessionId;
      // Two different things, so two outcomes: no session to start is not a
      // coalesce, and a caller may want to tell them apart.
      if (!effectiveSessionId) {
        logger.warn({
          tag: 'chronicle_start_no_session',
          event_type: 'CHRONICLE_START_NO_SESSION',
          details: { origin: args.origin },
        });
        // An in-flight start owns the tracking entry; only a first call closes it.
        if (!isStarting) {
          closeOwnedStartTracking(
            args.tracking,
            new DesktopCaptureError(
              DesktopCaptureFailure.SESSION_NOT_FOUND,
              'Cannot start Chronicle recording without a session id'
            )
          );
        }
        return 'no_session';
      }
      if (isStarting) {
        // Silent in the metric by design — the winner owns the attempt — so log
        // it, or a resume the device never acted on leaves no trace at all.
        logger.warn({
          tag: 'chronicle_start_coalesced',
          event_type: 'CHRONICLE_START_COALESCED',
          details: { origin: args.origin, sessionId: effectiveSessionId },
        });
        return 'already_starting';
      }
      setIsStarting(true);

      // Track Datadog duration vital for all origins.
      // For desktop-initiated starts, RecordingButton.handleStart() already calls
      // this — calling again is idempotent. For device-initiated starts, this is
      // the only place it gets called.
      datadogRum.startDurationVital(DatadogDurationVital.SESSION_START_RECORDING);

      let step = '';
      try {
        // When the v2 flag is on but the EKS base URL is missing, fall back to
        // v1 — recording is more important than the cutover. The rule and the
        // which-resolver trap live on `resolveEffectiveTranscriptionService`.
        const { effectiveUseV2, transcriptionBaseUrl, didFallBackToV1 } =
          resolveEffectiveTranscriptionService(useTranscriptionServiceV2);
        if (didFallBackToV1) {
          logger.warn({
            msg: 'Chronicle: v2 flag on but transcription base URL is null — falling back to v1',
            tag: TRANSCRIPTION_V2_FALLBACK_TAG,
            surface: 'chronicle_live',
          });
        }

        // 1. Generate recording ID — v2 uses a client-generated UUID; v1
        //    lets ml-scribe assign the id and returns it in the response.
        //    For v2, the EKS `PUT /recordings/{id}` registration is fired
        //    by Rust inside `OnlineSession::start_session` after warmup —
        //    NOT here. Firing it here AND in Rust would register the same
        //    recording_id twice over the network for every Chronicle start.
        step = 'generate_recording_id';
        let recordingId: string;
        if (effectiveUseV2) {
          recordingId = crypto.randomUUID();
        } else {
          const res = await requestRecordingId(effectiveSessionId);
          if (!res.data.recording_id) {
            trackFailedToStartChunkRecording('Chronicle: Failed to get recording id');
            throw new Error('Failed to get recording id');
          }
          recordingId = res.data.recording_id;
        }

        // 2. Get auth token and base URL for upload pipeline
        step = 'get_auth_credentials';
        // APP-4062: recovery-on-401 (the `EKS_REGISTER_UNAUTHORIZED` retry below) is
        // the refresh path — this read does not pre-emptively force one.
        let token = await getAuthTokenForHttpClient();
        const baseUrl = getScribeBaseUrlFromV2();
        if (!token || !baseUrl) {
          throw new Error('Auth token or base URL missing');
        }

        // 3. Stop desktop idle stream to release system mic and free resources.
        //    Chronicle uses its own microphone so there's no contention, but the
        //    idle stream is unnecessary overhead once recording starts.
        step = 'stop_idle_stream';
        await releaseIdleStreamForCapture(effectiveSessionId);

        // Capture existing session duration before any cache mutation or
        // IPC, so step 7 below uses an authoritative pre-start baseline.
        const existingDuration =
          queryClient.getQueryData<SessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId: effectiveSessionId },
          ])?.duration ?? 0;

        // 4. Start session in Rust — configures upload pipeline AND starts
        //    recording on the Chronicle device in a single IPC call.
        //    For device-initiated starts, the device call is idempotent.
        //    Recording filename is auto-captured by the Rust state watcher.
        //    Rust also inserts the sync_tracking live_recording record.
        //
        //    Claim chronicleSessionId + origin BEFORE the IPC: Rust's
        //    `start_recording` updates DeviceState to Recording synchronously
        //    and emits `chronicle:state_changed(recording)` mid-IPC. Without
        //    the early claim, `useChronicleSessionControl`'s useEffect
        //    observes the device echo while chronicleSessionId is still null
        //    and — because the optimistic cache update has already set
        //    `recording_status=RECORDING` — fires a false "Heidi Remote is
        //    recording offline" toast (APP-4673).
        step = 'start_session';
        setChronicleSessionId(effectiveSessionId);
        setRecordingOrigin(args.origin);
        setActiveRecordingTransport('chronicle');
        const trigger =
          args.origin === CHRONICLE_ORIGIN.DEVICE
            ? ('device_start' as const)
            : existingDuration > 0
              ? ('app_resume' as const)
              : ('app_start' as const);
        const invokeStartSession = (sessionToken: string) =>
          chronicleSessionBridge.startSession({
            sessionId: effectiveSessionId,
            recordingId,
            baseUrl,
            token: sessionToken,
            userId: getCurrentUser()?.user_ref_id ?? undefined,
            // APP-3779: Rust locks the version for the lifetime of this online
            // session. A flag flip after start has no effect on chunks already
            // in flight or queued for upload — Rust reads these locked values,
            // not the live flag.
            useTranscriptionServiceV2: effectiveUseV2,
            transcriptionBaseUrl: effectiveUseV2 ? transcriptionBaseUrl : null,
            languages: effectiveUseV2 ? languages : [],
            rustAuth0TokenRefresh,
            trigger,
          });
        try {
          await invokeStartSession(token);
        } catch (err) {
          // APP-3779 (davejcampbell review): single retry on
          // `EKS_REGISTER_UNAUTHORIZED`. The pre-refresh above covers the
          // common case (stale Redux token); this catches the rarer case
          // where the just-refreshed token is also rejected (clock skew,
          // backend revocation, propagation lag). Force-refresh once more
          // and retry — same recording_id, so the second register PUT is
          // idempotent on the EKS side.
          // Duck-check the bridge error's `errorCode` rather than `instanceof
          // ChronicleSessionBridgeError` — `errorCode` is unique to the bridge
          // and survives module-boundary identity issues that `instanceof`
          // can hit (e.g. under jest's module isolation when the bridge
          // module is mocked).
          const errorCode = (err as { errorCode?: unknown })?.errorCode;
          // APP-7607: a concurrent device-initiated start already armed the
          // online session for this physical recording under a different
          // session_id. Adopt that active session instead of clobbering it or
          // surfacing a failure. Returning here (not throwing) also skips the
          // `step === 'start_session'` rollback that would null chronicleSessionId
          // and re-open the device-echo guard in useChronicleSessionControl.
          if (errorCode === 'CHRONICLE_ONLINE_SESSION_ALREADY_ACTIVE') {
            const activeSessionId = await chronicleSessionBridge
              .getSessionState()
              .then((s) => s.sessionId)
              .catch(() => null);
            logger.warn({
              tag: 'chronicle_online_session_already_active',
              event_type: 'CHRONICLE_ONLINE_SESSION_ALREADY_ACTIVE',
              details: {
                attempted_session_id: effectiveSessionId,
                active_session_id: activeSessionId,
              },
            });
            if (activeSessionId) {
              setChronicleSessionId(activeSessionId);
              setActiveSessionId(activeSessionId);
            } else {
              // Couldn't resolve the active session — at least don't leave the
              // UI pointing at this duplicate's id.
              setChronicleSessionId(null);
            }
            closeOwnedStartTracking(args.tracking, activeSessionId ? undefined : err);
            return 'already_active';
          }
          if (
            errorCode === 'CHRONICLE_CAPABILITY_UNRESOLVED' ||
            errorCode === 'CHRONICLE_CHUNK_PULL_DISABLED'
          ) {
            // Rust already emitted Datadog scribe.remote.start_refused;
            // FE folds Amplitude into catalogue device_event (gated).
            trackRemoteRecordingStartBlocked({
              sessionId: effectiveSessionId,
              sessionIdLocal: effectiveSessionId,
              trigger,
              reason:
                errorCode === 'CHRONICLE_CAPABILITY_UNRESOLVED'
                  ? 'capability_unresolved'
                  : 'chunk_pull_disabled',
            });
            throw err;
          }
          if (errorCode !== 'EKS_REGISTER_UNAUTHORIZED') {
            throw err;
          }
          logger.warn({
            tag: 'chronicle_eks_register_unauthorized_retry',
            event_type: 'CHRONICLE_EKS_REGISTER_UNAUTHORIZED_RETRY',
            details: {
              session_id: effectiveSessionId,
              recording_id: recordingId,
            },
          });
          const refreshedToken = await forceRefreshDesktopAuthToken();
          if (!refreshedToken) {
            // Refresh failed — propagate the original error to the user.
            throw err;
          }
          token = refreshedToken;
          await invokeStartSession(refreshedToken);
        }
        // After this point Rust is recording. Bump `step` so a throw from
        // any post-IPC sync call does NOT trigger the rollback below — that
        // would null the JS atoms while Rust kept recording.
        step = 'post_start_session';
        closeOwnedStartTracking(args.tracking);

        setActiveSessionId(effectiveSessionId);

        // 6. Update session recording status in query cache.
        //    For desktop-initiated starts this is redundant (already set by
        //    useRecordingButton), but essential for device-initiated starts.
        queryClient.setQueryData<SessionResponse>(
          [GET_SESSION_BY_ID, { sessionId: effectiveSessionId }],
          (old) => {
            if (!old) return undefined;
            return { ...old, recording_status: RecordingStatus.RECORDING };
          }
        );

        // 7. Start timer — offset by existing duration so resume continues
        //    from accumulated time (APP-4680).
        const elapsedSinceStartedAt = args.startedAt
          ? Math.max(
              0,
              Math.floor((Date.now() - new Date(args.startedAt).getTime()) / 1000)
            )
          : 0;
        dispatch.timerModel.onStart(
          Math.max(existingDuration + elapsedSinceStartedAt, args.resumeFromSeconds ?? 0)
        );

        // 8. Track analytics — sessionStartRecording with 'Chronicle' as device
        sessionStartRecording({
          sessionId: effectiveSessionId,
          transcribeMode: queryClient.getQueryData<SessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId: effectiveSessionId },
          ])?.transcribe_mode,
          device: 'Chronicle',
          // APP-3779: tag rollout metrics with the locked version.
          transcriptionServiceVersion: effectiveUseV2 ? 'v2' : 'v1',
        });
        trackHardwareRecordingStart({
          sessionIdLocal: effectiveSessionId,
          sessionId: effectiveSessionId,
          origin: args.origin,
        });

        {
          const jotaiStore = getDefaultStore();
          const device = jotaiStore.get(chronicleDeviceStateAtom);
          const isChunkLive = jotaiStore.get(chronicleSupportsChunkLiveAtom);
          trackRemotePipelineSelected({
            sessionId: effectiveSessionId,
            sessionIdLocal: effectiveSessionId,
            pipeline: mapRouteToPipeline(isChunkLive ? 'chunk_pull' : 'stream'),
            trigger,
            firmwareVersion: device.firmwareVersion,
          });
        }

        /* Detail-entry resume analog (HAR-1334): a desktop-initiated start
           that continues a session with recorded duration — the capture
           device (Remote) is confirmed here. Active-session pause→resume
           goes through useChronicleResumeRecording instead and must not
           fire this; device-initiated starts are the accepted blind spot,
           matching mobile. */
        if (existingDuration > 0 && args.origin === CHRONICLE_ORIGIN.DESKTOP) {
          trackSessionDetailResumeStarted({
            session_id: effectiveSessionId,
            session_id_local: effectiveSessionId,
            previous_duration: existingDuration,
            recording_device: 'remote',
          });
        }

        logger.debug('[useChronicleStartRecording] Recording started', {
          sessionId: effectiveSessionId,
          recordingId,
          origin: args.origin,
        });
        return 'started';
      } catch (error) {
        if (step !== 'post_start_session') {
          closeOwnedStartTracking(args.tracking, error);
        }
        logger.error('[useChronicleStartRecording] Failed at step:', {
          step,
          error,
          sessionId: effectiveSessionId,
        });
        dispatch.timerModel.onPause();

        // Roll back the early-claimed Chronicle session ONLY when the IPC
        // itself failed (step is still 'start_session'). For pre-IPC
        // failures the atoms were never set in this invocation, and a
        // concurrent invocation could legitimately own them. For post-IPC
        // failures Rust is already recording — clearing them would
        // mismatch UI/Rust state.
        if (step === 'start_session') {
          setChronicleSessionId(null);
          setRecordingOrigin(null);
          setActiveRecordingTransport(null);
        }

        // Reset recording status in query cache on failure
        queryClient.setQueryData<SessionResponse>(
          [GET_SESSION_BY_ID, { sessionId: effectiveSessionId }],
          (old) => {
            if (!old) return undefined;
            return { ...old, recording_status: RecordingStatus.IDLE };
          }
        );

        const errorMessage = error instanceof Error ? error.message : String(error);
        trackFailedToStartChunkRecording(`Chronicle: ${errorMessage}`);
        trackHardwareRecordingStartFailed({
          sessionIdLocal: effectiveSessionId,
          origin: args.origin,
          errorType: step || 'unknown',
          errorDescription: errorMessage,
        });

        toast({
          tone: 'destructive',
          title: t('settings.chronicle.notification.startRecordingErrorTitle'),
          description: isHeidiSupportReachable
            ? t('settings.chronicle.notification.startRecordingErrorDescription')
            : formatMessageOutsideReact(startRecordingErrorMessages.contactIt),
        });

        throw error;
      } finally {
        setIsStarting(false);
      }
    },
    [
      sessionId,
      isStarting,
      dispatch,
      queryClient,
      setChronicleSessionId,
      setRecordingOrigin,
      setActiveRecordingTransport,
      setActiveSessionId,
      t,
      useTranscriptionServiceV2,
      rustAuth0TokenRefresh,
      languages,
    ]
  );

  return { startChronicleRecording, isStarting };
}
